import React, { createContext, useState, useContext, ReactNode, useCallback, useRef, useEffect } from 'react';

type SettingsSidebarContextType = {
  isOpen: boolean;
  open: () => void;
  close: () => void;
};

const SettingsSidebarContext = createContext<SettingsSidebarContextType | undefined>(undefined);

export const SettingsSidebarProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [isOpen, setIsOpen] = useState(false);
  const closeTimerRef = useRef<NodeJS.Timeout | null>(null);

  const clearTimer = useCallback(() => {
    if (closeTimerRef.current) {
      clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }
  }, []);

  const open = useCallback(() => {
    clearTimer();
    setIsOpen(true);
    window.dispatchEvent(new CustomEvent('sidebar-opened', { detail: { type: 'settings' } }));
  }, [clearTimer]);

  const close = useCallback(() => {
    clearTimer();
    setIsOpen(false);
  }, [clearTimer]);

  // Handle other sidebars opening
  useEffect(() => {
    const handleSidebarOpened = (event: CustomEvent) => {
      if (event.detail.type !== 'settings') {
        clearTimer();
        setIsOpen(false);
      }
    };
    window.addEventListener('sidebar-opened', handleSidebarOpened as EventListener);
    return () => {
      window.removeEventListener('sidebar-opened', handleSidebarOpened as EventListener);
    };
  }, [clearTimer]);

  // Support for closing nested components if needed
  useEffect(() => {
    return () => clearTimer();
  }, [clearTimer]);

  return (
    <SettingsSidebarContext.Provider 
      value={{ isOpen, open, close }}
    >
      {children}
    </SettingsSidebarContext.Provider>
  );
};

export const useSettingsSidebar = () => {
  const context = useContext(SettingsSidebarContext);
  if (context === undefined) {
    throw new Error('useSettingsSidebar must be used within a SettingsSidebarProvider');
  }
  return context;
};
